<template>
  <!-- Super admin overview: how the platform is doing, which clients need a look, and shortcuts (US 9436) -->
  <div class="adm-page">
    <section class="home-hero adm-hero">
      <div class="home-hero__main">
        <div class="home-hero__eyebrow">{{ $t('admin_page.eyebrow') }}</div>
        <h1 class="home-hero__title" qid="administration-page-title">{{ $t('administration.title') }}</h1>
        <p class="home-hero__summary" qid="administration-summary">
          <template v-if="!loading">{{ $t('admin_page.overview.summary', { clients: counts.clients, users: counts.users, active: counts.active_users }) }}</template>
          <template v-else>&nbsp;</template>
        </p>

        <div class="home-search mt-search"
             :class="{ 'mt-search--open': searchOpen && query.trim() }"
             @keydown.down.prevent="moveResult(1)"
             @keydown.up.prevent="moveResult(-1)"
             @keydown.enter.prevent="chooseResult()"
             @keydown.esc="closeSearch"
        >
          <font-awesome-icon icon="search" class="home-search__icon"/>
          <input ref="search"
                 v-model="query"
                 class="home-search__input"
                 :placeholder="$t('admin_page.overview.search_placeholder')"
                 :aria-label="$t('admin_page.overview.search_placeholder')"
                 autocomplete="off"
                 qid="administration-search"
                 @input="search"
                 @focus="searchOpen = true"
                 @blur="closeSearchSoon"
          />
          <span class="home-kbd mt-search__kbd">{{ isMac ? '⌘K' : 'Ctrl+K' }}</span>
          <button type="button" class="mt-search__cancel" @mousedown.prevent="closeSearch">{{ $t('maintenance_overview.search.cancel') }}</button>

          <div v-if="searchOpen && query.trim()" class="home-search__results mt-search__results">
            <template v-for="group in resultGroups">
              <div :key="'g-' + group.key" class="home-search__group">{{ $t('admin_page.overview.search_' + group.key) }}</div>
              <div v-for="item in group.items"
                   :key="group.key + '-' + item.id"
                   class="home-search__result mt-search__result"
                   :class="{ 'is-active': flatResults.indexOf(item) === activeResult }"
                   @mousedown.prevent="chooseResult(item)"
              >
                <img :src="avatarUrl(item)"
                     class="adm-avatar adm-avatar--sm"
                     :class="{ 'adm-avatar--round': group.key === 'users' }"
                     alt="">
                <span class="mt-search__text">
                  <span class="home-search__name">{{ item.name }}</span>
                  <span class="home-search__meta">{{ resultMeta(group.key, item) }}</span>
                </span>
              </div>
            </template>
            <div v-if="searching && !flatResults.length" class="home-search__empty">
              <font-awesome-icon icon="spinner" class="fa-spin"/>
            </div>
            <div v-else-if="!flatResults.length" class="home-search__empty">{{ $t('admin_page.overview.search_empty', { query: query.trim() }) }}</div>
          </div>
        </div>

        <div class="home-hero__chips">
          <router-link to="/administration/clients/create" class="home-chip home-chip--light" qid="administration-new-client">
            <font-awesome-icon icon="plus"/>{{ $t('admin_page.actions.new_client') }}
          </router-link>
          <router-link to="/administration/users/create" class="home-chip" qid="administration-new-user">
            <font-awesome-icon icon="plus"/>{{ $t('admin_page.actions.new_user') }}
          </router-link>
          <b-dropdown no-caret
                      variant="link"
                      toggle-class="home-chip"
                      class="adm-chip-dropdown"
                      qid="administration-download-logs"
          >
            <template #button-content>
              <font-awesome-icon icon="cloud-download-alt"/>{{ $t('admin_page.exports.menu') }}
              <font-awesome-icon icon="angle-down"/>
            </template>
            <b-dropdown-item v-for="report in exports" :key="report.key" @click="download(report)">
              {{ $t('admin_page.exports.' + report.key + '.title') }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <div v-if="!loading" class="home-week adm-health" qid="administration-health">
        <div class="home-week__top">
          <svg class="home-ring"
               width="96"
               height="96"
               viewBox="0 0 96 96"
               aria-hidden="true">
            <circle class="home-ring__track"
                    cx="48"
                    cy="48"
                    r="42"/>
            <circle v-if="counts.active_clients"
                    class="home-ring__value"
                    cx="48"
                    cy="48"
                    r="42"
                    :stroke-dasharray="ringDash(counts.active_clients, counts.clients, 42)"
            />
          </svg>
          <b class="home-week__count adm-health__count">{{ counts.active_clients }}/{{ counts.clients }}</b>
          <div>
            <div class="home-week__title">{{ $t('admin_page.overview.health_title') }}</div>
            <div class="home-week__text">{{ $t('admin_page.overview.health_text') }}</div>
          </div>
        </div>
        <router-link v-if="attention.trial_ending.count"
                     class="us-health__line"
                     :to="{ name: 'administration.clients', query: { status: 'trial' } }">
          <span class="us-dot us-dot--amber"></span>{{ $tc('admin_page.overview.health_trials', attention.trial_ending.count, { count: attention.trial_ending.count }) }}
        </router-link>
        <router-link v-if="attention.inactive.count"
                     class="us-health__line"
                     :to="{ name: 'administration.clients', query: { status: 'inactive' } }">
          <span class="us-dot us-dot--red"></span>{{ $tc('admin_page.overview.health_inactive', attention.inactive.count, { count: attention.inactive.count }) }}
        </router-link>
      </div>
    </section>

    <!-- Platform in numbers; each tile opens the filtered list -->
    <div class="adm-stats">
      <router-link v-for="tile in tiles"
                   :key="tile.key"
                   :to="tile.to"
                   class="adm-stat"
                   :qid="'administration-stat-' + tile.key"
      >
        <span class="adm-stat__icon" :class="'is-' + tile.accent"><font-awesome-icon :icon="tile.icon"/></span>
        <span class="adm-stat__text">
          <span class="adm-stat__label">{{ $t('admin_page.overview.stats.' + tile.key) }}</span>
          <b class="adm-stat__value">{{ loading ? '–' : tile.value }}</b>
          <span class="adm-stat__sub">{{ loading ? '' : tile.sub }}</span>
        </span>
      </router-link>
    </div>

    <div class="adm-body">
      <div class="adm-main">
        <!-- Newest clients -->
        <section class="v2-card">
          <div class="v2-card__head">
            <h3>{{ $t('admin_page.overview.newest_title') }}</h3>
            <router-link :to="{ name: 'administration.clients', query: { sort: 'created_at' } }" class="v2-link">{{ $t('admin_page.overview.see_all') }}</router-link>
          </div>
          <router-link v-for="client in newest"
                       :key="'n-' + client.id"
                       :to="{ name: 'administration.clientView', params: { slug: client.slug } }"
                       class="adm-row"
                       qid="administration-newest-client"
          >
            <img :src="avatarUrl(client)" class="adm-avatar" alt="">
            <span class="adm-row__main">
              <b>{{ client.name }}</b>
              <span class="adm-row__sub">{{ $t('admin_page.overview.created', { date: fromNow(client.created_at) }) }}</span>
            </span>
            <span class="adm-row__facts">
              <span><font-awesome-icon icon="users"/> {{ client.users_count }}</span>
              <span v-if="client.partitions_count"><font-awesome-icon icon="layer-group"/> {{ client.partitions_count }}</span>
            </span>
            <TrialPill :client="client"/>
          </router-link>
          <div v-if="!loading && !newest.length" class="home-empty home-empty--small">{{ $t('system.no_results') }}</div>
        </section>

        <!-- Recently signed in -->
        <section class="v2-card">
          <div class="v2-card__head">
            <h3>{{ $t('admin_page.overview.recent_users_title') }}</h3>
            <router-link :to="{ name: 'administration.users', query: { activity: 'active' } }" class="v2-link">{{ $t('admin_page.overview.see_all') }}</router-link>
          </div>
          <router-link v-for="user in recentUsers"
                       :key="'u-' + user.id"
                       :to="{ name: 'administration.userView', params: { slug: user.slug } }"
                       class="adm-row"
                       qid="administration-recent-user"
          >
            <img :src="avatarUrl(user)" class="adm-avatar adm-avatar--round" alt="">
            <span class="adm-row__main">
              <b>{{ user.name }}</b>
              <span class="adm-row__sub">{{ user.email }}</span>
            </span>
            <span class="adm-row__when" v-b-tooltip.hover :title="formatDateTime(user.last_login_at)">{{ fromNow(user.last_login_at) }}</span>
          </router-link>
          <div v-if="!loading && !recentUsers.length" class="home-empty home-empty--small">{{ $t('system.no_results') }}</div>
        </section>
      </div>

      <div class="adm-side">
        <!-- Needs attention: one block per reason, with up to five clients -->
        <section class="v2-card">
          <div class="v2-card__head">
            <h3>{{ $t('maintenance_overview.attention.title') }}</h3>
            <span v-if="attentionTotal" class="mt-badge">{{ attentionTotal }}</span>
          </div>
          <template v-for="block in attentionBlocks">
            <div :key="'a-' + block.key" class="v2-att adm-att">
              <span class="v2-att__icon" :class="'v2-att__icon--' + block.color"><font-awesome-icon :icon="block.icon"/></span>
              <span class="v2-att__text">
                {{ $tc('admin_page.attention.' + block.key, block.count, { count: block.count }) }}
              </span>
              <router-link v-if="block.status && block.count > block.items.length"
                           :to="{ name: 'administration.clients', query: { status: block.status } }"
                           class="v2-link">{{ $t('admin_page.overview.see_all') }}</router-link>
            </div>
            <router-link v-for="client in block.items"
                         :key="block.key + '-' + client.id"
                         :to="{ name: 'administration.clientView', params: { slug: client.slug } }"
                         class="adm-att__client"
                         :qid="'administration-attention-' + block.key"
            >
              <span>{{ client.name }}</span>
              <span class="adm-att__when">{{ attentionDetail(block.key, client) }}</span>
            </router-link>
          </template>
          <div v-if="!loading && !attentionTotal" class="home-empty home-empty--small">{{ $t('admin_page.attention.empty') }}</div>
        </section>

        <!-- Areas of the super admin menu -->
        <section class="v2-card">
          <div class="v2-card__head"><h3>{{ $t('admin_page.overview.areas_title') }}</h3></div>
          <router-link v-for="area in areas"
                       :key="area.key"
                       :to="area.to"
                       class="mt-row__link adm-area"
                       :qid="'administration-area-' + area.key"
          >
            <font-awesome-icon :icon="area.icon" class="mt-row__icon" fixed-width/>
            <span class="mt-row__name">{{ $t(area.label) }}</span>
            <span v-if="area.count !== undefined && !loading" class="v2-tag v2-tag--grey">{{ area.count }}</span>
            <font-awesome-icon icon="angle-right" class="mt-row__chevron"/>
          </router-link>
        </section>

        <!-- Log lists -->
        <section class="v2-card">
          <div class="v2-card__head"><h3>{{ $t('admin_page.exports.title') }}</h3></div>
          <div v-for="report in exports" :key="report.key" class="v2-att">
            <span class="v2-att__icon v2-att__icon--blue"><font-awesome-icon icon="file-alt"/></span>
            <span class="v2-att__text">
              {{ $t('admin_page.exports.' + report.key + '.title') }}
              <small class="mt-att__sub">{{ $t('admin_page.exports.' + report.key + '.text') }}</small>
            </span>
            <button type="button"
                    class="us-icon-btn"
                    v-b-tooltip.hover
                    :title="$t('admin_page.exports.download')"
                    :aria-label="$t('admin_page.exports.download')"
                    :disabled="downloading === report.key"
                    :qid="'administration-export-' + report.key"
                    @click="download(report)"
            >
              <font-awesome-icon :icon="downloading === report.key ? 'spinner' : 'cloud-download-alt'" :class="{ 'fa-spin': downloading === report.key }"/>
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import {config} from '@/config.js';
import {userAvatarUrl} from '@/util/userAvatar.js';
import TrialPill from '@/components/administration/TrialPill.vue';

const moment = require('moment');

// The three statistics exports that used to be the whole page
const EXPORTS = [
  { key: 'clients', url: 'log-list', file: 'export-log-list.xlsx' },
  { key: 'users', url: 'user-log-list', file: 'export-user-log-list.xlsx' },
  { key: 'mapping', url: 'mapping-log-list', file: 'export-mapping-log-list.xlsx' }
];

export default {
  name: 'AdministrationOverview',
  components: {
    TrialPill
  },
  data() {
    return {
      loading: true,
      counts: { clients: 0, partitions: 0, users: 0, active_users: 0, never_signed_in: 0, active_clients: 0, trial: 0, paying: 0, features: 0, guides: 0 },
      attention: {
        trial_ending: { count: 0, items: [] },
        trial_ended: { count: 0, items: [] },
        inactive: { count: 0, items: [] },
        no_users: { count: 0, items: [] }
      },
      newest: [],
      recentUsers: [],
      exports: EXPORTS,
      downloading: null,
      query: '',
      searchOpen: false,
      searching: false,
      results: { clients: [], users: [] },
      activeResult: 0,
      isMac: /Mac|iPhone|iPad/.test(navigator.platform)
    }
  },
  computed: {
    tiles() {
      let c = this.counts;
      return [
        { key: 'clients', icon: 'building', accent: 'teal', value: c.clients, to: { name: 'administration.clients' },
          sub: this.$t('admin_page.overview.stats.clients_sub', { paying: c.paying, trial: c.trial }) },
        { key: 'partitions', icon: 'layer-group', accent: 'violet', value: c.partitions, to: { name: 'administration.clients' },
          sub: this.$t('admin_page.overview.stats.partitions_sub') },
        { key: 'users', icon: 'users', accent: 'blue', value: c.users, to: { name: 'administration.users' },
          sub: this.$t('admin_page.overview.stats.users_sub', { active: c.active_users }) },
        { key: 'never_signed_in', icon: 'user-slash', accent: 'amber', value: c.never_signed_in, to: { name: 'administration.users', query: { activity: 'never' } },
          sub: this.$t('admin_page.overview.stats.never_signed_in_sub') }
      ];
    },
    attentionBlocks() {
      let blocks = [
        { key: 'trial_ending', status: 'trial', color: 'amber', icon: 'hourglass-half' },
        { key: 'trial_ended', status: 'trial_ended', color: 'red', icon: 'clock' },
        { key: 'inactive', status: 'inactive', color: 'violet', icon: 'user-slash' },
        { key: 'no_users', status: null, color: 'blue', icon: 'users' }
      ];
      return blocks
        .map(block => Object.assign(block, this.attention[block.key]))
        .filter(block => block.count);
    },
    attentionTotal() {
      return this.attentionBlocks.reduce((total, block) => total + block.count, 0);
    },
    areas() {
      return [
        { key: 'clients', label: 'dashboard.clients', icon: 'layer-group', to: '/administration/clients', count: this.counts.clients },
        { key: 'users', label: 'dashboard.users', icon: 'user-cog', to: '/administration/users', count: this.counts.users },
        { key: 'features', label: 'administration.features.title', icon: 'tags', to: '/administration/features', count: this.counts.features },
        { key: 'guide', label: 'dashboard.guide', icon: 'book-open', to: '/administration/guide', count: this.counts.guides },
        { key: 'settings', label: 'dashboard.settings', icon: 'cogs', to: '/administration/settings' }
      ];
    },
    resultGroups() {
      return ['clients', 'users']
        .map(key => ({ key: key, items: this.results[key] }))
        .filter(group => group.items.length);
    },
    flatResults() {
      return this.resultGroups.reduce((all, group) => all.concat(group.items), []);
    }
  },
  methods: {
    loadOverview() {
      let self = this;
      piincHttp.get('administration/overview').then(function(response) {
        self.counts = response.data.counts;
        self.attention = response.data.attention;
        self.newest = response.data.newest;
        self.recentUsers = response.data.recent_users;
        self.loading = false;
      }, function() {
        self.loading = false;
      });
    },
    avatarUrl(item) {
      return userAvatarUrl(item);
    },
    ringDash(done, total, radius) {
      let circumference = 2 * Math.PI * radius;
      let length = total ? circumference * Math.min(done / total, 1) : 0;
      return length + ' ' + circumference;
    },
    fromNow(date) {
      return date ? moment(date).fromNow() : '';
    },
    formatDateTime(date) {
      return this.$options.filters.formatDateTime(date, config.dateFormat + ' HH:mm');
    },
    attentionDetail(key, client) {
      if (key === 'trial_ending' || key === 'trial_ended') {
        let date = this.$options.filters.formatDate(client.trial_ends_at, config.dateFormat);
        return this.$t(key === 'trial_ending' ? 'admin_page.attention.ends_on' : 'admin_page.attention.ended_on', { date: date });
      }
      if (key === 'inactive') {
        return this.$t('admin_page.attention.last_sign_in', { date: this.fromNow(client.last_login_at) });
      }
      return this.$t('admin_page.overview.created', { date: this.fromNow(client.created_at) });
    },
    resultMeta(group, item) {
      if (group === 'users') {
        return item.email;
      }
      let parts = [];
      if (item.parent) {
        parts.push(this.$t('admin_page.overview.partition_of', { client: item.parent.name }));
      }
      if (item.cvr) {
        parts.push(item.cvr);
      }
      return parts.join(' · ');
    },
    search: _.debounce(function() {
      let self = this;
      let keyword = this.query.trim();
      if (!keyword) {
        this.results = { clients: [], users: [] };
        return;
      }
      this.searching = true;
      Promise.all([
        piincHttp.get('clients', { params: { keyword: keyword, per_page: 5 } }),
        piincHttp.get('users', { params: { keyword: keyword, per_page: 5 } })
      ]).then(function(responses) {
        // A slower answer for an older query must not replace newer results
        if (keyword !== self.query.trim()) {
          return;
        }
        self.results = { clients: responses[0].data.items, users: responses[1].data.items };
        self.activeResult = 0;
        self.searching = false;
      }, function() {
        self.searching = false;
      });
    }, 250),
    moveResult(step) {
      let count = this.flatResults.length;
      if (count) {
        this.activeResult = (this.activeResult + step + count) % count;
      }
    },
    chooseResult(item) {
      item = item || this.flatResults[this.activeResult];
      if (!item) {
        return;
      }
      let isUser = this.results.users.indexOf(item) !== -1;
      this.closeSearch();
      this.$router.push({ name: isUser ? 'administration.userView' : 'administration.clientView', params: { slug: item.slug } });
    },
    closeSearch() {
      this.searchOpen = false;
      this.$refs.search && this.$refs.search.blur();
    },
    closeSearchSoon() {
      setTimeout(() => { this.searchOpen = false; }, 150);
    },
    // ⌘K / Ctrl+K focuses the search from anywhere on the page
    onKeydown(event) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        this.$refs.search && this.$refs.search.focus();
      }
    },
    download(report) {
      let self = this;
      this.downloading = report.key;
      piincHttp.get(report.url, { responseType: 'blob' }).then(function(response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', report.file);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        self.downloading = null;
      }, function() {
        self.downloading = null;
      });
    }
  },
  created() {
    this.loadOverview();
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  }
}
</script>
